import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  Link,
  Container,
  Fade,
  Slide,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email,
  Lock,
  Login as LoginIcon,
  Psychology,
  AutoAwesome,
  SmartToy,
} from '@mui/icons-material';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [animationTime, setAnimationTime] = useState(0);
  
  const { login, isAuthenticated, error, clearError } = useAuth();
  
  // Debug: Log error state changes
  useEffect(() => {
    console.log('Login component - error state changed:', error);
  }, [error]);
  const navigate = useNavigate();
  const location = useLocation();

  // Keine Animation mehr

  const from = location.state?.from?.pathname || '/dashboard';

  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  useEffect(() => {
    return () => clearError();
  }, [clearError]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!formData.email) newErrors.email = 'E-Mail ist erforderlich';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'E-Mail ist ungültig';
    if (!formData.password) newErrors.password = 'Passwort ist erforderlich';
    if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }
    
    // Fehler vor dem Login löschen
    clearError();
    
    const result = await login(formData.email, formData.password);
    if (result.success) {
      navigate(from, { replace: true });
    }
    // Fehler wird automatisch im AuthContext gesetzt und über error State angezeigt
  };

  const handleTogglePasswordVisibility = () => setShowPassword(!showPassword);

  // Keine bewegenden Objekte mehr

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100vw',
        backgroundImage: 'url(/cosmos-7693800.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: { xs: 1, sm: 2 },
        m: 0,
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflow: 'auto',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.7)',
          zIndex: 0,
        },
        // iPhone-spezifische Optimierungen
        WebkitOverflowScrolling: 'touch',
        touchAction: 'pan-y',
      }}
    >
      <Container 
        maxWidth="xs" 
        sx={{ 
          position: 'relative', 
          zIndex: 2, 
          px: { xs: 1, sm: 2 },
          py: { xs: 2, sm: 0 },
          width: '100%',
          maxWidth: { xs: '95vw', sm: '400px' }
        }}
      >
        <Fade in timeout={800}>
          <Card
            sx={{
              borderRadius: { xs: 3, sm: 4 },
              boxShadow: '0 25px 50px rgba(0, 0, 0, 0.35)',
              background: 'rgba(255,255,255,0.95)',
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255, 215, 0, 0.35)',
              width: '100%',
              // iPhone-spezifische Touch-Optimierungen
              WebkitTapHighlightColor: 'transparent',
              touchAction: 'manipulation',
            }}
          >
            <CardContent sx={{ 
              p: { xs: 2.5, sm: 3, md: 4 },
              '&:last-child': { pb: { xs: 2.5, sm: 3, md: 4 } }
            }}>
              <Box sx={{ textAlign: 'center', mb: { xs: 2, sm: 3 } }}>
                <Slide in timeout={1000}>
                  <Box>
                    <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
                      <img 
                        src="/logo.png" 
                        alt="KiChatbot Logo" 
                        style={{ 
                          height: '96px', 
                          width: 'auto',
                          maxWidth: '100%'
                        }} 
                      />
                    </Box>
                    <Typography 
                      variant="h4" 
                      sx={{ 
                        fontWeight: 700, 
                        mb: 1,
                        fontSize: { xs: '1.75rem', sm: '2.125rem' }
                      }}
                    >
                      KiChatbot
                    </Typography>
                    <Typography 
                      variant="body1" 
                      color="text.secondary"
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                    >
                      Admin Panel
                    </Typography>
                  </Box>
                </Slide>
              </Box>

              {error && (
                <Alert 
                  severity="error" 
                  sx={{ 
                    mb: 2, 
                    borderRadius: 2,
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    backgroundColor: '#ffebee',
                    color: '#c62828',
                    border: '1px solid #ffcdd2',
                    '& .MuiAlert-icon': {
                      color: '#d32f2f'
                    }
                  }}
                  onClose={() => clearError()}
                >
                  {error}
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmit}>
                <TextField 
                  fullWidth 
                  label="E-Mail" 
                  name="email" 
                  type="email" 
                  value={formData.email} 
                  onChange={handleChange} 
                  error={!!errors.email} 
                  helperText={errors.email} 
                  sx={{ 
                    mb: 2,
                    '& .MuiInputBase-input': {
                      fontSize: { xs: '16px', sm: '1rem' }, // Verhindert Zoom auf iOS
                    }
                  }} 
                  InputProps={{ 
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email color="action" />
                      </InputAdornment>
                    ) 
                  }} 
                />
                <TextField 
                  fullWidth 
                  label="Passwort" 
                  name="password" 
                  type={showPassword ? 'text' : 'password'} 
                  value={formData.password} 
                  onChange={handleChange} 
                  error={!!errors.password} 
                  helperText={errors.password} 
                  sx={{ 
                    mb: 3,
                    '& .MuiInputBase-input': {
                      fontSize: { xs: '16px', sm: '1rem' }, // Verhindert Zoom auf iOS
                    }
                  }} 
                  InputProps={{ 
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock color="action" />
                      </InputAdornment>
                    ), 
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton 
                          onClick={handleTogglePasswordVisibility} 
                          edge="end"
                          sx={{ 
                            touchAction: 'manipulation',
                            minWidth: '44px', // Mindestgröße für Touch
                            minHeight: '44px'
                          }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ) 
                  }} 
                />
                <Button 
                  type="submit" 
                  fullWidth 
                  variant="contained" 
                  size="large" 
                  startIcon={<LoginIcon />} 
                  sx={{ 
                    py: { xs: 1.5, sm: 1.4 }, 
                    mb: 2, 
                    textTransform: 'none', 
                    fontWeight: 600,
                    fontSize: { xs: '1rem', sm: '1.1rem' },
                    minHeight: '48px', // Mindestgröße für Touch
                    touchAction: 'manipulation',
                    WebkitTapHighlightColor: 'transparent',
                    '&:active': {
                      transform: 'scale(0.98)', // Touch-Feedback
                    }
                  }}
                >
                  Anmelden
                </Button>
              </Box>

              <Box sx={{ textAlign: 'center' }}>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                >
                  Noch kein Konto?{' '}
                  <Link 
                    component={RouterLink} 
                    to="/register" 
                    sx={{ 
                      color: '#FFD700', 
                      textDecoration: 'none', 
                      fontWeight: 600, 
                      '&:hover': { textDecoration: 'underline' },
                      touchAction: 'manipulation',
                      minHeight: '44px',
                      display: 'inline-flex',
                      alignItems: 'center'
                    }}
                  >
                    Jetzt registrieren
                  </Link>
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Fade>
      </Container>
    </Box>
  );
};

export default Login;
